import { Injectable, OnDestroy, inject } from '@angular/core';
import { OAuthService, OAuthEvent } from 'angular-oauth2-oidc';
import { BehaviorSubject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TranslateService } from '@ngx-translate/core';

import { AuthService } from './auth.service';
import { TokenRefreshModalComponent } from '../components/token-refresh-modal/token-refresh-modal.component';
import { APP_CONFIG } from '../../config/app-config.token';
import { AppConfig } from '../../config/app-config.model';

/**
 * MobileTokenRefreshService
 *
 * Monitors token expiration and manages token refresh flow for mobile platforms.
 * Based on the production-tested desktop app's TokenRefreshService.
 *
 * Features:
 * - Listens for token_expires events from OAuthService
 * - Shows modal dialog 30 seconds before token expiration
 * - Handles token refresh with retry logic (max 2 retries)
 * - Provides user-friendly session expiry experience
 *
 * This service ensures users don't lose their work due to silent token expiration.
 */
@Injectable({
  providedIn: 'root'
})
export class MobileTokenRefreshService implements OnDestroy {
  private oauthService = inject(OAuthService);
  private authService = inject(AuthService);
  private dialogService = inject(DialogService);
  private translateService = inject(TranslateService);
  private config = inject<AppConfig>(APP_CONFIG);


  private tokenCheckSubscription: Subscription;
  private refreshInProgress = new BehaviorSubject<boolean>(false);
  private retryCount = 0;
  private readonly maxRetries = 2;

  // Reference to the modal dialog (only one can be shown at a time)
  ref: DynamicDialogRef | undefined;

  /**
   * Start monitoring token expiration
   * Call this in app initialization or after successful login
   */
  startTokenRefreshCheck(): void {
    // Listen for token_expires events from OAuthService
    this.tokenCheckSubscription = this.oauthService.events
      .pipe(filter((event: OAuthEvent) => event.type === 'token_expires'))
      .subscribe({
        next: () => {
          // Only show modal if:
          // 1. Not already refreshing
          // 2. Token is actually expiring (within configured threshold)
          if (!this.refreshInProgress.value) {
            const expiresAt = this.authService.getTokenExpiration();
            const now = Date.now();
            const timeUntilExpiry = expiresAt ? expiresAt - now : 0;

            // Show modal if token expires within the configured threshold
            // Default: 30 seconds (like desktop app)
            const thresholdMs = (this.config.keycloakTokenExpirationCheck || 30) * 1000;
            if (timeUntilExpiry > 0 && timeUntilExpiry <= thresholdMs) {
              this.refreshInProgress.next(true);
              this.showModal();
            }
          }
        },
        error: err => console.error('[MobileTokenRefreshService] OAuth event error (token_expires):', err)
      });
  }

  /**
   * Stop monitoring token expiration
   * Call this on app destruction or logout
   */
  stopTokenRefreshCheck(): void {
    if (this.tokenCheckSubscription) {
      this.tokenCheckSubscription.unsubscribe();
    }

    if (this.ref) {
      this.ref.close();
      this.ref = undefined;
    }
  }

  ngOnDestroy(): void {
    this.stopTokenRefreshCheck();
  }

  /**
   * Show token expiry modal dialog
   * @private
   */
  private showModal(): void {
    // Prevent multiple modals from being shown
    if (this.ref) {
      console.warn('[MobileTokenRefreshService] Modal already shown, skipping');
      return;
    }

    this.translateService.get('TOKEN_REFRESH.title').subscribe({
      next: title => {
        this.ref = this.dialogService.open(TokenRefreshModalComponent, {
          header: title,
          width: '50vw',
          contentStyle: { overflow: 'auto' },
          breakpoints: {
            '960px': '75vw',
            '640px': '90vw'
          },
          closable: false, // User must choose an action
          data: {
            onContinue: () => this.refreshToken(),
            onLogout: () => this.logout()
          }
        });

        this.ref.onClose.subscribe({
          next: () => {
            this.refreshInProgress.next(false);
            this.ref = undefined;
          },
          error: err => console.error('[MobileTokenRefreshService] Modal onClose error:', err)
        });
      },
      error: err => console.error('[MobileTokenRefreshService] Translation error:', err)
    });
  }

  /**
   * Logout user
   * @private
   */
  private logout(): void {
    this.authService.logout();
  }

  /**
   * Refresh access token using refresh token
   * Implements retry logic (max 2 retries) like desktop app
   * @private
   */
  private refreshToken(): void {
    this.authService.refreshToken()
      .then(refreshed => {
        if (refreshed) {
          this.resetRetry();

          // Close modal on success
          if (this.ref) {
            this.ref.close();
          }
        } else {
          this.resetRetry();

          // Close modal since token is still valid
          if (this.ref) {
            this.ref.close();
          }
        }
      })
      .catch(error => {
        console.error('[MobileTokenRefreshService] ✗ Token refresh failed:', error);

        // Retry logic
        if (this.retryCount < this.maxRetries) {
          this.retryCount++;
          console.warn(`[MobileTokenRefreshService] Retrying token refresh (${this.retryCount}/${this.maxRetries})`);

          // Wait 1 second before retry
          setTimeout(() => {
            this.refreshToken();
          }, 1000);
        } else {
          console.error('[MobileTokenRefreshService] Max retries reached, logging out user');
          this.logout();
        }
      })
      .finally(() => {
        this.refreshInProgress.next(false);
      });
  }

  /**
   * Reset retry counter after successful refresh
   * @private
   */
  private resetRetry(): void {
    this.retryCount = 0;
  }
}
